import { useCallback, useContext, useMemo, useEffect, Dispatch, SetStateAction } from "react"
import { isEmpty, size } from "lodash"
import Filters from "components/analyze-header-filters"
import { VIEW_TYPES } from "helpers/base"
import { combineTanStackStatuses, DataStatus } from "helpers/data-statuses"
import { computeUpdatedFilters, computeAddedFilters } from "helpers/filters"
import { computeFilterOns } from "helpers/filters/filter-ons"
import { NORMALIZATION } from "helpers/segments"
import { useAccountAlias } from "resources/account"
import { useAssetFilters, useSecuritiesDescriptors } from "resources/filters"
import { useTagCategories } from "resources/tags"
import { useAttributeCategories } from "resources/attributes"
import { useClassificationsData } from "resources/classifications"
import { useAnalytics } from "connected-components/analytics"
import { Filter, FilterSecurity, IDashboardContext } from "connected-components/dashboard/context/types"
import useFilterSecuritiesData from "hooks/use-filter-securities-data"
import { DASHBOARD_EVENTS } from "../../analytics"

export default function FilterSelector({
  context,
  setFilterSelectorStatus,
  hasMultipleInstruments,
  anchor,
  toggleFilters,
}: FilterSelectorProps) {
  const { modelId, instrument, secondInstrument, comparisonInstrument, endDate, base, filter, setFilter, trades, normalization, setNormalization, isExpanded, isIntraday } = useContext(context)
  const analytics = useAnalytics()
  const account = useAccountAlias()

  const isActive = !!base

  const [assetFilters, assetFiltersStatus] = useAssetFilters(modelId, account, instrument, endDate, endDate, isIntraday)

  // This creates a TICKER.MIC id from each security in the filter
  const securitiesIds: string[] = useMemo(() => Object.values(filter?.securities || {}).reduce((acc: string[], filterTypeSecurities: FilterSecurity[]): string[] =>
    [...acc, ...filterTypeSecurities.map(({ ticker, mic, country }) => `${ticker}.${mic || country}`)], []),
  [filter])

  const [filterSecurities, filterSecuritiesStatus] = useFilterSecuritiesData(
    modelId,
    account,
    instrument,
    secondInstrument,
    comparisonInstrument,
    endDate,
    isExpanded,
  )

  const [tagCategories, tagCategoriesStatus] = useTagCategories(instrument)
  const [attributeCategories, attributeCategoriesStatus] = useAttributeCategories(instrument)
  const [securitiesDescriptors, securitiesDescriptorsStatus] = useSecuritiesDescriptors(account, modelId, securitiesIds, endDate)

  const {
    classifications,
    classificationsValues,
    flattenedClassificationsValues,
    status: classificationsStatus,
  } = useClassificationsData(endDate, filter)

  const filterOns = useMemo(() => {
    if (isEmpty(assetFilters)) return []

    // [TODO:TS] Fix when typing the base.js file
    const viewType = (isActive ? VIEW_TYPES.ACTIVE : VIEW_TYPES.ABSOLUTE) as "absolute" | "active"
    return computeFilterOns(
      viewType,
      assetFilters,
      filter,
      undefined,
      tagCategories,
      classifications?.map(({ versions, ...classification }) => ({ ...classification, version: versions[versions.length - 1] })) || [],
      classificationsValues,
      attributeCategories,
      instrument?.type,
      normalization,
      hasMultipleInstruments,
    )
  }, [isActive, assetFilters, filter, tagCategories, classifications, classificationsValues, attributeCategories, instrument?.type, normalization, hasMultipleInstruments])

  const handleUpdateFilters = useCallback((filters: unknown) => {
    // [TODO:TS] Type function
    const { filter }: { filter: Filter | null } = computeUpdatedFilters(filters)

    // Track filter changes
    if (filter && Object.keys(filter).length > 0) {
      analytics.track(DASHBOARD_EVENTS.FILTER_APPLIED, { filter })
    } else if (isEmpty(filter)) {
      analytics.track(DASHBOARD_EVENTS.FILTER_CLEARED)
    }

    setFilter(filter)
  }, [setFilter, analytics])

  const status = combineTanStackStatuses([
    classificationsStatus,
    filterSecuritiesStatus,
    tagCategoriesStatus,
    attributeCategoriesStatus,
    assetFiltersStatus,
    securitiesDescriptorsStatus,
  ])

  // This component's visual handle is actually the Filter button inside the Dashboard component. We expose the status to the
  // Dashboard component so it can disable the button while it's loading or if there's an error.
  useEffect(() => {
    setFilterSelectorStatus(status)
  }, [status, setFilterSelectorStatus])

  // [TODO:TS] Type function
  const addedFilters = useMemo(() => {
    return computeAddedFilters(filter, filterOns, {
      tagCategories,
      securitiesDescriptors,
      attributeCategories,
      classificationsInfos: {
        classificationsMetas: classifications,
        classificationsValues: flattenedClassificationsValues,
      },
      portfolioFilters: assetFilters,
    })
  }, [
    filter,
    filterOns,
    tagCategories,
    classifications,
    securitiesDescriptors,
    assetFilters,
    attributeCategories,
    flattenedClassificationsValues,
  ])

  const hasTrades = size(trades) > 0
  // [TODO] startDate, intraday, isExpanded, currency, hasUnsupportedContributionFilter
  return anchor && (
    <Filters
      account={account}
      assetId={instrument?.id}
      assetType={instrument?.type}
      endDate={endDate}
      filterOns={filterOns}
      heapAddFilterId="dashboard"
      modelId={modelId}
      securityIds={filterSecurities}
      normalization={hasTrades ? NORMALIZATION.CONTRIBUTION : normalization}
      setNormalization={setNormalization}
      disableNormalization={hasTrades}
      disableNormalizationTooltip={hasTrades ? "Standalone normalization is not supported for trades" : null}
      addedFilters={addedFilters}
      updateFilters={handleUpdateFilters}
      anchorElement={anchor}
      filtersVisible={!!anchor}
      toggleFilters={toggleFilters}
      // [TODO:TS] Workaround because typescript misunderstand proptypes
      startDate={undefined}
      intraday={undefined}
      isExpanded={undefined}
      currency={undefined}
      hasUnsupportedContributionFilter={undefined}
    />
  )
}

interface FilterSelectorProps {
  context: IDashboardContext
  setFilterSelectorStatus: Dispatch<SetStateAction<DataStatus>>
  hasMultipleInstruments: boolean
  anchor: HTMLElement | null
  toggleFilters: () => void
}
